import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import Moment from 'moment';
import { StyleSheet, View, ScrollView } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import BackgroundImage from '../../components/BackgroundImage';
import YearlyParametersSection from './components/YearlyParametersSection';
import AirQualitySection from './components/AirQuality';
import TopSection, { screenMaxWidth, TopSectionProps } from '../../components/TopSection';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import { MetricPropTypes, MetricProps } from '../../graphs/AirQualityGraph';
import { DateRange, DateSelectionRange, updateDateRange } from '../../redux/dateRangeSlice';
import { updateDateComparisonRange } from '../../redux/dateComparisonRangeSlice';
import { HappyScoreCategory, HappyScoreCategoryTitles, Metric } from '../../constants/Types';
import isMobile from '../../components/IsMobile';
import MobileHeader from './components/MobileHeader';
import OverviewMobile from './components/OverviewMobile';
import GetMetric, { AggregationFunction } from '../../requests/Sensors/GetMetric';
import MetricSelectionRow from './components/MetricSelectionRow';
import ParametersInfoContent from './components/ParametersInfoContent';
import FixedDateComponent from '../../components/FixedDateComponent';

export default function InternalEnvironmentScreen({ route }) {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const dispatch = useDispatch();
  const scrollViewRef = useRef(null);
  const graphRef = useRef<View>(null);
  const setRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [floorId, setFloorId] = useState<string | null>(null);
  const [areaId, setAreaId] = useState<string | null>(null);
  const [sensorName, setSensorName] = useState<string | null>(null);
  const [deepLinkMetric, setDeepLinkMetric] = useState<number | null>(null);
  const [locationExternalId, setLocationExternalId] = useState<string | null>(null);

  const onConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setRange(
      { start, end, selectedRange },
    ),
    [setRange],
  );

  useEffect(() => {
    setTimeout(() => {
      const graph = (route.params as any)?.metricIndex;
      if (graphRef.current && (graph === 0 || graph)) {
        graphRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      }
    }, 500);
  }, [route]);

  const onCompareConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setCompareRange(
      { start, end, selectedRange },
    ),
    [setCompareRange],
  );

  useEffect(() => {
    setAssetId((route?.params as any)?.assetId);
    setFloorId((route?.params as any)?.floorId);
    setAreaId((route?.params as any)?.areaId);
    setSensorName((route?.params as any)?.sensorName);

    const metricIndex = (route.params as any)?.metricIndex;
    if (metricIndex === 0 || metricIndex) {
      setDeepLinkMetric(metricIndex);
    }
  }, [route]);

  const topSectionProps: TopSectionProps = {
    screen: HappyScoreCategory.INTERNAL_ENVIRONMENT,
  };

  const defaultThresholdColors: string[] = [
    Colors.light.hereworksGreen300,
    Colors.light.hereworksYellow500,
    Colors.light.hereworksOrange500,
    Colors.light.hereworksRed500,
    Colors.light.hereworksPurple500,
    Colors.light.hereworksPurple800,
  ];

  const metrics: MetricProps[] = [
    {
      table: Metric.TEMPERATURE,
      title: 'Temperature',
      unit: '°C',
      thresholds: [
        { title: 'Good', value: '18-23', color: defaultThresholdColors[1] },
        { title: 'Optimal', value: '21', color: defaultThresholdColors[0] },
      ],
      description: 'Temperature is the level of heat in the indoor environment, measured in degrees Celsius or Fahrenheit. Extreme temperatures can cause discomfort and health issues. The optimal range for general comfort is 20°C to 24°C (68°F to 75°F), but adjustments may be needed based on preferences and activities.',
      scoreValue: '4_01_happyScore',
    },
    {
      table: Metric.HUMIDITY,
      title: 'Humidity',
      unit: '%',
      scoreValue: '4_03_happyScore',
      type: MetricPropTypes.PERCENTAGE,
      thresholds: [
        { title: 'Low', value: '0-29', color: defaultThresholdColors[1] },
        { title: 'Optimal', value: '30-60', color: defaultThresholdColors[0] },
        { title: 'High', value: '>61', color: defaultThresholdColors[2] },
      ],
      description: 'Humidity is the amount of water vapor in the air, measured as a percentage (relative humidity). It greatly affects indoor comfort and well-being. High humidity can lead to stickiness, mold growth, and respiratory issues, while low humidity can cause dry skin and respiratory irritation. Recommended indoor relative humidity is between 30% to 50%.',
    },
    {
      table: Metric.AIR_PRESSURE,
      title: 'Air Pressure',
      unit: 'bar',
      scoreValue: null,
      description: 'Atmospheric pressure, measured in hectopascals (hPa), refers to the weight of the air exerted on a surface. Low pressure can be associated with headaches, dizziness, or fatigue, while high pressure may cause discomfort in sensitive individuals. A typical indoor pressure is around 1013 hPa, and significant deviations from this can influence human comfort and building dynamics.',
    },
    {
      table: Metric.CO2,
      title: 'CO2',
      unit: 'ppm',
      fullTitle: 'CO₂',
      scoreValue: '4_02_happyScore',
      thresholds: [
        { title: 'Good', value: '0', color: defaultThresholdColors[0] },
        { title: 'Moderate', value: '1000', color: defaultThresholdColors[1] },
        { title: 'Unhealthy if sensitive', value: '1500', color: defaultThresholdColors[2] },
        { title: 'Unhealthy', value: '2000', color: defaultThresholdColors[3] },
        { title: 'Very unhealthy', value: '2500', color: defaultThresholdColors[4] },
        { title: 'Hazardous', value: '3000', color: defaultThresholdColors[5] },
      ],
      description: 'Carbon dioxide (CO2) concentration is a key indicator of indoor air quality and ventilation. Elevated levels of CO2 can cause drowsiness, headaches, and cognitive impairment. The ideal indoor CO2 concentration is below 800 ppm, with levels above 1000 ppm indicating inadequate ventilation and potential health effects.',
    },
    {
      table: Metric.TVOC,
      title: 'TVOC',
      unit: 'ppb',
      scoreValue: '4_18_happyScore',
      thresholds: [
        { title: 'Good', value: '0', color: defaultThresholdColors[0] },
        { title: 'Moderate', value: '220', color: defaultThresholdColors[1] },
        { title: 'Unhealthy if sensitive', value: '660', color: defaultThresholdColors[2] },
        { title: 'Unhealthy', value: '2200', color: defaultThresholdColors[3] },
        { title: 'Very unhealthy', value: '3300', color: defaultThresholdColors[4] },
        { title: 'Hazardous', value: '4400', color: defaultThresholdColors[5] },
      ],
      description: 'TVOC - Total Volatile Organic Compounds.\nVOCs are molecules that evaporate at normal indoor atmospherio conditions, many of which are irritating or carcinogenic, or can cause headaches, fatigue, nausea, or allergio reactions sources of harmful VOCs include paints and solvents, cleaners and disinfectants, pesticides, and air fresheners',
    },
    // {
    //   table: Metric.PM1,
    //   title: 'PM1.0',
    //   unit: 'µg/m3',
    //   scoreValue: null,
    // eslint-disable-next-line max-len
    //   description: 'PM1.0 refers to ultra-fine particulate matter with a diameter of 1 micrometer or smaller. These particles can penetrate deep into the lungs and even enter the bloodstream, potentially leading to serious health problems. Indoor PM1.0 levels should ideally remain below 5 µg/m³ to protect health.',
    // },
    {
      table: Metric.PM25,
      title: 'PM2.5',
      unit: 'µg/m3',
      scoreValue: '4_09_happyScore',
      thresholds: [
        { title: 'Good', value: '0', color: defaultThresholdColors[0] },
        { title: 'Moderate', value: '12.1', color: defaultThresholdColors[1] },
        { title: 'Unhealthy if sensitive', value: '35.5', color: defaultThresholdColors[2] },
        { title: 'Unhealthy', value: '55.5', color: defaultThresholdColors[3] },
        { title: 'Very unhealthy', value: '150.5', color: defaultThresholdColors[4] },
        { title: 'Hazardous', value: '250.5', color: defaultThresholdColors[5] },
      ],
      description: 'PM2.5 is a grouping of particles with an aerodynamic diameter of 2.5 um or less, capable of penetrating deep into our lungs and even entering our bloodstream. These particles can come from natural or human-made sources, like vehicle exhaust, wildfires, power plant emissions, or other combustion activities, and are strongly implicated in reductions in health and lifespan.',
    },
    // {
    //   table: Metric.PM4,
    //   title: 'PM4.0',
    //   unit: 'µg/m3',
    //   scoreValue: null,
    // eslint-disable-next-line max-len
    //   description: 'PM4.0 refers to particulate matter with a diameter of 4 micrometers or smaller. These particles are small enough to be inhaled and can affect respiratory health, causing irritation and exacerbating conditions such as asthma. Recommended indoor PM4.0 levels should remain below 12 µg/m³ to minimize health risks.',
    // },
    {
      table: Metric.PM10,
      title: 'PM10',
      unit: 'µg/m3',
      scoreValue: null,
      description: 'Also known as coarse particles, PM10 is defined as all particles with an aerodynamic diameter of 10 um or smaller. Some common examples of PM10 are mold spores, bacteria, dust, smoke, and airborne viral particles. These particles can penetrate into our lungs and irritate your airways, nose, throat, and eyes.',
    },
    {
      table: Metric.NOISE,
      title: 'Noise',
      unit: 'dB',
      scoreValue: '4_10_happyScore',
      thresholds: [
        { title: 'Good', value: '0', color: defaultThresholdColors[0] },
        { title: 'Moderate', value: '60', color: defaultThresholdColors[1] },
        { title: 'Damaging after 2 hours', value: '80', color: defaultThresholdColors[2] },
        { title: 'Damaging after 50 mins', value: '95', color: defaultThresholdColors[3] },
        { title: 'Hearing loss after 15 mins', value: '100', color: defaultThresholdColors[4] },
        { title: 'Hearing loss after <5 mins', value: '105', color: defaultThresholdColors[5] },
      ],
      description: 'Noise, measured in decibels (dB), quantifies the intensity of sound in a space. Excessive noise can disrupt concentration, cause stress, and lead to hearing damage over time. For indoor environments, it is recommended to keep noise levels below 40 dB for quiet activities like reading, and below 50 dB for general activities. Prolonged exposure to levels above 70 dB can be harmful.',
    },
    {
      table: Metric.LIGHT,
      title: 'Light',
      unit: 'lx',
      scoreValue: '4_16_happyScore',
      description: 'Light in IAQI is the space\'s illumination level, measured in lux (lx). Adequate lighting is crucial for visibility, comfort, and well-being. Insufficient light strains the eyes, while excessive brightness causes glare. For general indoor lighting, 300 to 500 lux is recommended.',
    },
    {
      table: Metric.FORMALDEHYDE,
      title: 'Formaldehyde',
      unit: 'µg/m3',
      scoreValue: null,
      thresholds: [
        { title: 'Low', value: '<9', color: defaultThresholdColors[0] },
        { title: 'Moderate', value: '9-50', color: defaultThresholdColors[1] },
        { title: 'High', value: '>50', color: defaultThresholdColors[2] },
      ],
      description: 'Formaldehyde concentration, measured in parts per million (ppm), indicates the presence of this volatile organic compound (VOC) which is found in many household products and building materials. Exposure to formaldehyde can cause irritation of the eyes, nose, throat, and skin, and long-term exposure has been linked to cancer. Recommended indoor formaldehyde levels should not exceed 0.08 ppm for safety.',
    },
    {
      table: Metric.WATER_VAPOUR,
      title: 'Water Vapour Density',
      unit: 'ppm',
      scoreValue: '4_03_happyScore',
      description: 'Water vapor density measures the actual amount of water vapor in the air, expressed in grams per cubic meter (g/m³). This metric is important for understanding moisture levels and the potential for condensation, mold growth, or discomfort. The ideal indoor water vapor density should be between 5-15 g/m³ for a healthy, comfortable environment.',
    },
    {
      table: Metric.OCCUPANCY,
      title: 'Occupancy',
      unit: '',
      scoreValue: '2_05_happyScore',
      excludeLocation: true,
      aggregationFunction: AggregationFunction.MAX,
      description: 'Occupancy measures the number of people in a space at any time. Tracking occupancy helps adjust air quality, temperature, and ventilation to match occupant needs. Higher occupancy can increase CO₂ levels and discomfort, so managing it improves occupant comfort and energy efficiency.',
    },
  ];

  const [liveValues] = useState<number[]>(metrics.map(() => null));
  const [graphData, setGraphData] = useState<object[][]>(metrics.map(() => []));
  const [graphComparisonData, setGraphComparisonData] = useState<object[][]>(metrics.map(() => []));
  const [selectedMetric, setSelectedMetric] = useState<MetricProps>(null);
  const metricTitles: string[] = metrics.map((metric: MetricProps) => metric.title);

  useEffect(() => {
    if (!isMobile) return;
    metrics.forEach((metric: MetricProps, index: number) => {
      GetMetric({
        metric: metric.table,
        range: {
          start: Moment().subtract(60, 'minutes').toDate().toISOString(),
          end: Moment().toDate().toISOString(),
        },
        interval: '1m',
        aggregationFunction: metric.aggregationFunction,
      })?.then((data) => {
        if (!(data?.length > 0)) return;
        if (data.source) {
          liveValues[index] = data.source / (metric.factor ?? 1);
        } else {
          const dataPoints = data?.filter((item) => typeof item.value === 'number')
            .map((item) => item.value);
          const value: number = dataPoints ? dataPoints[dataPoints.length - 1] : null;
          liveValues[index] = value > -1 ? value : null;
        }
      });
    });
  }, []);

  useEffect(() => {
    const newData = metrics.map(() => []);
    metrics?.forEach((metric: MetricProps, metricIndex: number) => {
      GetMetric({ metric: metric.table, range, externalId: locationExternalId })
        ?.then((metricData) => {
          (metricData ?? []).forEach((e, index: number) => newData[metricIndex]?.push({
            value: (e.value ?? 0) / (metric.factor ?? 1),
            date: e.timestamp,
            index,
          }));
          setGraphData([...newData]);
        });
    });
  }, [range, locationExternalId]);

  useEffect(() => {
    const newData = metrics.map(() => []);
    metrics?.forEach((metric: MetricProps, metricIndex: number) => {
      GetMetric({ metric: metric.table, range: compareRange, externalId: locationExternalId })
        ?.then((metricData) => {
          (metricData ?? []).forEach((e, index: number) => newData[metricIndex]?.push({
            value: (e.value ?? 0) / (metric.factor ?? 1),
            date: e.timestamp,
            index,
          }));
          setGraphComparisonData([...newData]);
        });
    });
  }, [compareRange, locationExternalId]);

  if (isMobile) {
    return <MobileHeader
      happyScoreMetric='group4InternalEnvironment'
      screen={HappyScoreCategory.INTERNAL_ENVIRONMENT}
      overview={<OverviewMobile
        {...{ metrics, liveValues, setSelectedMetric }}
        parametersInfoContent={<ParametersInfoContent />}
      />}
      showContent={() => setSelectedMetric(null)}
      graphTitle={selectedMetric?.title}
      graphData={graphData[metricTitles.indexOf(selectedMetric?.title)]}
      graphComparisonData={graphComparisonData[metricTitles.indexOf(selectedMetric?.title)]}
      historyFooter={<MetricSelectionRow
        {...{ setSelectedMetric }}
        titles={metrics.map((metric: MetricProps) => metric.title)}
        selectedTitle={selectedMetric?.title}
        select={(index: number) => setSelectedMetric(metrics[index])}
      />}
      {...{ setLocationExternalId }}
    />;
  }

  return !notEmpty ? <ScreenEmptyState large /> : <View style={{ flex: 1 }}>
    <ScrollView style={styles.container}
      ref={scrollViewRef}>
      <BackgroundImage />
      <TopSection {...topSectionProps} />
      <View style={[styles.content, { maxWidth: screenMaxWidth }]} ref={graphRef}>
        <AirQualitySection
          title={HappyScoreCategoryTitles[HappyScoreCategory.INTERNAL_ENVIRONMENT]}
          overallScoreIndex='group4InternalEnvironment'
          {...{ range, compareRange, metrics }}
          locationSelectionShown
          selectedSensor={assetId}
          floorId={floorId}
          areaId={areaId}
          sensorName={sensorName}
          deepLinkMetric={deepLinkMetric}
        />
        <YearlyParametersSection
          {...{ range, compareRange }}
          showComparison={!!compareRange.start && !!compareRange.end}
        />
      </View>
    </ScrollView>
    <FixedDateComponent onConfirm={onConfirm}
      onCompareConfirm={onCompareConfirm}
      showExport={false}
      liveEnabled
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.light.hereworksBackground,
  },
  content: {
    paddingHorizontal: 40,
  },
});
